import React, { useEffect, useRef, useState } from 'react';
import { fmtMoney } from '../../utils/priceMath';
import { CAUSE_OPTIONS, TM_REASON_MIN, linkQty, reasonOk } from '../../utils/tmPricing';
import { stamp } from '../../utils/tmDefects';

const causeLabel = (code) => (CAUSE_OPTIONS.find((c) => c.code === code) || {}).label || (code ? String(code) : 'no cause recorded');
// API timestamps in the viewer's local time (tmDefects.stamp: the API sends UTC).
const when = stamp;
const pp = (v) => (v === null || v === undefined ? '—' : fmtMoney(v, { precise: true }));

/**
 * The defects a T&M line PRICES, from GET tm_defects (falls back to the line's own `defects` from
 * tm_line_actuals when that read failed): part defects on the line's part and ruined sheets its
 * nest shared.
 */
export function defectsForLine(line, defectsData) {
  const sid = line && line.so_line_id;
  const list = defectsData && Array.isArray(defectsData.defects) ? defectsData.defects : null;
  if (list) {
    return list.filter((d) => d.so_line_id === sid
      || (Array.isArray(d.sheet_lines) && d.sheet_lines.some((s) => s.so_line_id === sid)));
  }
  // tm_line_actuals' entry keeps the pricing fields at its top level and has no history.
  return ((line && line.defects) || []).map((e) => ({
    ...e, so_line_id: sid, suggest_shop_error: !!e.suggest_shop_error, job_live: true,
    charged: e.charged !== false, removed_links: [], history: [],
    pricing: e.defect_type === 'sheet' ? null : e,
    sheet_lines: e.defect_type === 'sheet' ? [{ so_line_id: sid, amount: e.amount, plate: e.plate, time: e.time, removed_with_remake: e.removed_with_remake }] : [],
  }));
}

function LinkRemove({ link, disabled, onRemove }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const inputRef = useRef(null);
  // The Remove button is replaced by the reason input: focus moves there, not to the page.
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  if (!open) {
    return <button type="button" className="btn btn-link btn-sm p-0 ms-2" disabled={disabled} onClick={() => setOpen(true)}>Remove</button>;
  }
  return (
    <span className="d-inline-flex gap-1 align-items-center ms-2">
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-sm"
        style={{ width: 220 }}
        aria-label={`Why remove link #${link.link_id}`}
        placeholder={`Why is the link wrong? (${TM_REASON_MIN}+ characters)`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button type="button" className="btn btn-sm btn-outline-danger" disabled={disabled || !reasonOk(reason)}
        onClick={async () => { const ok = await onRemove(link, reason.trim()); if (ok) { setOpen(false); setReason(''); } }}>
        Remove with PIN
      </button>
      <button type="button" className="btn btn-sm btn-link" onClick={() => { setOpen(false); setReason(''); }}>Cancel</button>
    </span>
  );
}

function ManualLink({ defect, jobs, disabled, onLink }) {
  const pr = defect.pricing || {};
  const open = Math.max(0, Math.floor(Number(pr.open_qty ?? defect.n) || 0));
  const [job, setJob] = useState('');
  const [qty, setQty] = useState(open > 0 ? String(open) : '');
  const [show, setShow] = useState(false);
  if (open <= 0) return null;
  if (!show) {
    return <button type="button" className="btn btn-link btn-sm p-0" disabled={disabled} onClick={() => setShow(true)}>Link a remake job by hand…</button>;
  }
  const q = Number(qty);
  const valid = /^\d+$/.test(String(job).trim()) && Number.isInteger(q) && q >= 1 && q <= open && String(job).trim() !== String(defect.job_id);
  const listId = `tm-link-jobs-${defect.defect_id}`;
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center mt-1">
      <label className="small mb-0" htmlFor={`tm-link-job-${defect.defect_id}`}>Remake job #</label>
      <input id={`tm-link-job-${defect.defect_id}`} type="text" inputMode="numeric" list={listId} className="form-control form-control-sm" style={{ width: 100 }} value={job} onChange={(e) => setJob(e.target.value)} />
      <datalist id={listId}>{(jobs || []).filter((j) => String(j) !== String(defect.job_id)).map((j) => <option key={j} value={j} />)}</datalist>
      <label className="small mb-0" htmlFor={`tm-link-qty-${defect.defect_id}`}>pieces (≤ {open})</label>
      <input id={`tm-link-qty-${defect.defect_id}`} type="text" inputMode="numeric" className="form-control form-control-sm" style={{ width: 70 }} value={qty} onChange={(e) => setQty(e.target.value)} />
      <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled || !valid}
        onClick={async () => {
          const ok = await onLink({ defect_id: defect.defect_id, makeup_job_id: Number(job), part_id: defect.part_id ?? undefined, qty: q, source: 'manual' });
          if (ok) { setShow(false); setJob(''); }
        }}>
        Link with PIN
      </button>
      <button type="button" className="btn btn-sm btn-link" onClick={() => setShow(false)}>Cancel</button>
    </div>
  );
}

/**
 * Scrap drill-down of ONE T&M line (P2-b): the line's shop-error summary and every defect it prices,
 * with its decision, remake links and candidates. The summary shows what the decisions REMOVE
 * (scrap.override_removed) — never the raw formula (override_raw / override.raw), which can be
 * negative and is not a price.
 *
 * Callbacks (the pricing window does the PIN + request and re-reads afterwards):
 *   onDecide(defect)                 open the Charged / Shop error dialog
 *   onLink({defect_id, makeup_job_id, part_id, qty, source})   → Promise<boolean>
 *   onRemoveLink(link, reason)       → Promise<boolean>
 */
export default function TmScrapDrilldown({ line, defectsData, editable = true, disabled = false, onDecide, onLink, onRemoveLink }) {
  const scrap = line && line.scrap;
  const defects = defectsForLine(line, defectsData);
  const jobs = Array.from(new Set([
    ...((line && line.pieces) || []).map((p) => p.job_id),
    ...defects.map((d) => d.job_id),
  ].filter((j) => j !== null && j !== undefined))).sort((a, b) => a - b);
  const locked = disabled || !editable;
  if (!scrap && defects.length === 0) {
    return <div className="small text-muted" data-testid={`tm-scrap-${line && line.so_line_id}`}>No defects on this line.</div>;
  }
  return (
    <div className="small" data-testid={`tm-scrap-${line.so_line_id}`}>
      <div className="fw-semibold mb-1">Scrap</div>
      {scrap && (
        <div className="d-flex flex-wrap gap-3 mb-1" data-testid={`tm-scrap-summary-${line.so_line_id}`}>
          {scrap.override_blocked
            ? <span className="text-danger">Shop error removed: can&apos;t be computed (see the red item)</span>
            : <span>Shop error removed <strong>{fmtMoney(scrap.override_removed)}</strong></span>}
          <span>Value before the shop-error override {fmtMoney(scrap.V_before_override)}</span>
          <span className={scrap.warn ? 'text-warning-emphasis fw-semibold' : ''}>
            Scrap premium {fmtMoney(scrap.premium)}{scrap.premium_pct != null ? ` (${scrap.premium_pct}%)` : ''}{scrap.warn ? ' — raises this price a lot' : ''}
          </span>
          {scrap.unit_price != null && (
            <span>Unit {fmtMoney(scrap.unit_price)} with scrap, {fmtMoney(scrap.unit_without_scrap)} without</span>
          )}
        </div>
      )}
      {!editable && <div className="text-muted mb-1">The order is not pending: decisions and links can&apos;t change here.</div>}
      <ul className="list-unstyled mb-0">
        {defects.map((d) => {
          const pr = d.pricing || {};
          const live = d.decision;
          const shopError = !!(live && live.not_charged);
          const cands = Array.isArray(pr.candidates) ? pr.candidates : [];
          const sheet = (d.sheet_lines || []).find((s) => s.so_line_id === line.so_line_id);
          return (
            <li key={d.defect_id} className="border rounded p-2 mb-1" data-testid={`tm-defect-${d.defect_id}`}>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <strong>Defect #{d.defect_id}</strong>
                <span>{d.defect_type === 'sheet' ? 'ruined sheet' : `${d.n} pc`}{d.station ? ` at ${d.station}` : ''} · job #{d.job_id}</span>
                <span className="text-muted">cause: {causeLabel(d.cause_code)}</span>
                {d.suggest_shop_error && !shopError && <span className="badge bg-info-subtle text-info-emphasis">cause suggests shop error</span>}
                <span className={`badge ${shopError ? 'bg-warning text-dark' : 'bg-secondary'}`}>{shopError ? "Shop error — not charged" : 'Charged'}</span>
                {pr.removed_with_remake && <span className="badge bg-light text-dark border" title="Its cost goes out with a removed dedicated remake">removed with the remake</span>}
                {sheet && sheet.removed_with_remake && <span className="badge bg-light text-dark border">removed with the remake</span>}
                {pr.unplaced && <span className="badge bg-danger-subtle text-danger-emphasis" title="Its station could not be placed on the route">station not placed</span>}
                {!d.job_live && <span className="badge bg-dark">job {d.job_status}</span>}
                <button type="button" className="btn btn-sm btn-outline-primary ms-auto" disabled={locked} onClick={() => onDecide && onDecide(d)}>
                  Charged or shop error…
                </button>
              </div>
              <div className="text-muted">
                Recorded{d.recorded_by ? ` by ${d.recorded_by}` : ''}{d.created_at ? ` ${when(d.created_at)}` : ''}{d.reason ? ` — “${d.reason}”` : ''}
                {live && ` · decided by ${live.decided_by || '?'}${live.decided_at ? ` ${when(live.decided_at)}` : ''}: ${live.reason || ''}`}
              </div>
              {d.defect_type !== 'sheet' && (pr.thru_pp != null || pr.down_pp != null) && (
                <div>Per piece on job #{d.job_id}: {pp(pr.thru_pp)} through the station, {pp(pr.down_pp)} after it{pr.open_qty != null ? ` · ${pr.open_qty} pc not linked to a remake` : ''}</div>
              )}
              {sheet && (
                <div>Ruined sheet on this line: {fmtMoney(sheet.amount)} (plate {fmtMoney(sheet.plate)}, time {fmtMoney(sheet.time)})</div>
              )}
              {(d.links || []).length > 0 && (
                <div className="mt-1">Remake links:
                  <ul className="mb-0 ps-3">
                    {d.links.map((lk) => (
                      <li key={lk.link_id}>
                        job #{lk.makeup_job_id} · {lk.qty} pc · {String(lk.source || '').replace(/_/g, ' ')}
                        {lk.ignored ? <span className="text-danger"> (ignored: {lk.ignored})</span> : null}
                        {lk.link_id != null && <LinkRemove link={lk} disabled={locked} onRemove={onRemoveLink} />}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.defect_type !== 'sheet' && cands.length > 0 && (
                <div className="mt-1">Possible remake jobs (a suggestion until confirmed):
                  <ul className="mb-0 ps-3">
                    {cands.map((c) => {
                      const q = linkQty(pr, c.job_id, c.pieces);
                      return (
                        <li key={c.job_id}>
                          job #{c.job_id} · {c.pieces} pc · {fmtMoney(c.value)}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2"
                            disabled={locked || q < 1}
                            onClick={() => onLink && onLink({ defect_id: d.defect_id, makeup_job_id: c.job_id, part_id: d.part_id ?? undefined, qty: q, source: 'heuristic_confirmed' })}
                          >
                            Confirm ({q} pc)
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {d.defect_type !== 'sheet' && !locked && <ManualLink defect={d} jobs={jobs} disabled={locked} onLink={onLink} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
